import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as DB from '@/lib/supabaseDb';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import PaymentFormModal from '@/components/feature/PaymentFormModal';
import { useToast } from '@/components/feature/Toast';
import { toTurkishLower } from '@/lib/customerSearch';

// ─── Helpers ────────────────────────────────────────────────
import { formatTRY } from '@/lib/currencyFormat';

function formatPhone(phone: string): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `0${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  if (digits.length === 11) return `0${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
  return phone;
}

function getWhatsAppUrl(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `https://wa.me/90${digits}`;
  if (digits.length === 11 && digits.startsWith('0')) return `https://wa.me/90${digits.slice(1)}`;
  if (digits.length === 11 && digits.startsWith('9')) return `https://wa.me/${digits}`;
  return `https://wa.me/90${digits.slice(-10)}`;
}

function getTelUrl(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `tel:+90${digits}`;
  if (digits.length === 11 && digits.startsWith('0')) return `tel:+90${digits.slice(1)}`;
  if (digits.length >= 10) return `tel:${digits}`;
  return `tel:${phone}`;
}

function getIstanbulDate(date?: Date): string {
  return (date || new Date()).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
}

type SourceType = 'installment' | 'note' | 'promise';
type FilterKey = 'all' | 'installment' | 'note' | 'promise' | 'today' | 'thisWeek' | 'thisMonth' | 'upcoming' | 'overdue' | 'paid';
type TabKey = 'tracking' | 'collected';
type CollectedFilter = 'all' | 'today' | 'thisWeek' | 'thisMonth';

interface UnifiedRecord {
  id: string;
  sourceType: SourceType;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerNo: string;
  saleId: string | null;
  saleNo: string | null;
  noteId: string | null;
  noteNo: string | null;
  noteBookNo?: string;
  notePageNo?: string;
  dueDate: string;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  status: string;
  installmentNo?: number;
  hasPromise: boolean;
  promiseId: string | null;
  promiseAmount: number | null;
  promiseDate: string | null;
  promiseStatus: string | null;
  promiseAssignedUser: string | null;
}

// ─── Status Badge ───────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    'Bekliyor': 'bg-amber-100 text-amber-700',
    'Ödendi': 'bg-emerald-100 text-emerald-700',
    'Gecikti': 'bg-red-100 text-red-700',
    'Kısmi Ödendi': 'bg-blue-100 text-blue-700',
    'İptal': 'bg-foreground-100 text-foreground-500',
    'Ertelendi': 'bg-secondary-100 text-secondary-700',
    'Ödeme Alındı': 'bg-emerald-100 text-emerald-700',
    'Kısmi Ödeme Yapıldı': 'bg-blue-100 text-blue-700',
    'Sözünü Tutmadı': 'bg-red-100 text-red-700',
    'Bugün Ödenecek': 'bg-amber-100 text-amber-700',
  };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${map[status] || 'bg-foreground-100 text-foreground-500'}`}>{status}</span>;
}

function SourceBadge({ type }: { type: SourceType }) {
  const map = {
    installment: { label: 'Taksit', icon: 'ri-calendar-check-line', cls: 'bg-primary-100 text-primary-700' },
    note: { label: 'Senet', icon: 'ri-file-list-3-line', cls: 'bg-accent-100 text-accent-700' },
    promise: { label: 'Ödeme Sözü', icon: 'ri-hand-heart-line', cls: 'bg-pink-100 text-pink-700' },
  };
  const m = map[type];
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap flex items-center gap-1 ${m.cls}`}>
      <i className={`${m.icon} text-[10px]`}></i>{m.label}
    </span>
  );
}

function SourceTypeBadge({ type }: { type: string | null }) {
  if (!type) return null;
  const map: Record<string, { label: string; cls: string }> = {
    'promissory_note': { label: 'Senet', cls: 'bg-accent-100 text-accent-700' },
    'legacy_debt': { label: 'Eski Borç', cls: 'bg-orange-100 text-orange-700' },
    'installment': { label: 'Taksit', cls: 'bg-primary-100 text-primary-700' },
  };
  const m = map[type] || { label: type, cls: 'bg-foreground-100 text-foreground-500' };
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${m.cls}`}>
      {m.label}
    </span>
  );
}

function PaymentMethodBadge({ method }: { method: string }) {
  const map: Record<string, { icon: string; cls: string }> = {
    'Nakit': { icon: 'ri-money-dollar-circle-line', cls: 'bg-emerald-100 text-emerald-700' },
    'Havale/EFT': { icon: 'ri-bank-line', cls: 'bg-blue-100 text-blue-700' },
    'Kredi Kartı': { icon: 'ri-bank-card-line', cls: 'bg-secondary-100 text-secondary-700' },
    'Çek': { icon: 'ri-coupon-line', cls: 'bg-amber-100 text-amber-700' },
  };
  const m = map[method] || { icon: 'ri-exchange-funds-line', cls: 'bg-foreground-100 text-foreground-600' };
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap flex items-center gap-1 ${m.cls}`}>
      <i className={`${m.icon} text-[10px]`}></i>{method}
    </span>
  );
}

// ─── Summary Card ───────────────────────────────────────────

function SummaryCard({ title, count, amount, icon, colorClass, active, onClick }: {
  title: string; count: number; amount: number; icon: string; colorClass: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`bg-background-50 rounded-xl border p-4 text-left transition-all cursor-pointer hover:shadow-sm ${
        active ? 'border-primary-300 ring-1 ring-primary-200' : 'border-background-200 hover:border-background-300'
      }`}
    >
      <div className="flex items-center gap-3 mb-2">
        <span className={`w-9 h-9 rounded-lg ${colorClass} flex items-center justify-center flex-shrink-0`}>
          <i className={`${icon} text-white text-sm`}></i>
        </span>
        <p className="text-xs font-medium text-foreground-400 leading-tight">{title}</p>
      </div>
      <div className="flex items-baseline justify-between">
        <p className="text-xl font-bold text-foreground-900 font-heading">{count > 0 ? count.toLocaleString('tr-TR') : '—'}</p>
        {amount > 0 && <p className="text-sm font-semibold text-foreground-600">{formatTRY(amount)}</p>}
      </div>
    </button>
  );
}

// ─── Main Page ──────────────────────────────────────────────

export default function PaymentsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { version } = useDataRefresh();
  const [searchParams] = useSearchParams();
  const srcParam = searchParams.get('src');
  const filterParam = searchParams.get('filter');
  const periodParam = searchParams.get('period');
  const statusParam = searchParams.get('status');

  // Data
  const [customers, setCustomers] = useState<any[]>([]);
  const [allSales, setAllSales] = useState<any[]>([]);
  const [installments, setInstallments] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [promises, setPromises] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [outstandingTotal, setOutstandingTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state - tabs
  const [activeTab, setActiveTab] = useState<TabKey>(
    statusParam === 'completed' || periodParam === 'today' ? 'collected' : 'tracking'
  );
  const [activeFilter, setActiveFilter] = useState<FilterKey>(
    filterParam === 'all_outstanding' ? 'all' : (filterParam as FilterKey) || 'all'
  );
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  // Collected tab state
  const [collectedSearch, setCollectedSearch] = useState('');
  const [collectedFilter, setCollectedFilter] = useState<CollectedFilter>(
    periodParam === 'today' ? 'today' : 'all'
  );
  const [collectedPage, setCollectedPage] = useState(1);
  const collectedPageSize = 15;

  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState<{ customerId: string; saleId?: string; amount?: number } | null>(null);
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all([
        DB.getCustomers(),
        DB.getSales(),
        DB.getInstallments(),
        DB.getPromissoryNotes(),
        DB.getPaymentPromises(),
        DB.getPayments(),
        DB.getTotalOutstandingReceivable(),
      ]);
      setCustomers((results[0].data as any[]) || []);
      setAllSales((results[1].data as any[]) || []);
      setInstallments((results[2].data as any[]) || []);
      setNotes((results[3].data as any[]) || []);
      setPromises((results[4].data as any[]) || []);
      setPayments((results[5].data as any[]) || []);
      if (results[6].data) {
        setOutstandingTotal((results[6].data as DB.OutstandingReceivable).total_receivable);
      }
      setError(null);
    } catch {
      setError('Veri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData, version]);

  // ─── Maps ───────────────────────────────────────────────

  const customerMap = useMemo(() => {
    const m: Record<string, any> = {};
    customers.forEach(c => { m[c.id] = c; });
    return m;
  }, [customers]);

  const saleMap = useMemo(() => {
    const m: Record<string, any> = {};
    allSales.forEach(s => { m[s.id] = s; });
    return m;
  }, [allSales]);

  const noteMap = useMemo(() => {
    const m: Record<string, any> = {};
    notes.forEach(n => { m[n.id] = n; });
    return m;
  }, [notes]);

  // ─── Date Constants ─────────────────────────────────────

  // Helper: Istanbul date object from YYYY-MM-DD string (UTC-based, no TZ issues)
  function istanbulDateFromString(dateStr: string): Date {
    const parts = dateStr.split('-').map(Number);
    return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  }

  const today = getIstanbulDate();
  const todayDate = istanbulDateFromString(today);

  // Calendar week helpers (Monday-based)
  function getMondayOfWeek(d: Date): Date {
    const day = d.getUTCDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(d);
    monday.setUTCDate(d.getUTCDate() + diff);
    return monday;
  }

  function getSundayOfWeek(d: Date): Date {
    const day = d.getUTCDay();
    const diff = day === 0 ? 0 : 7 - day;
    const sunday = new Date(d);
    sunday.setUTCDate(d.getUTCDate() + diff);
    return sunday;
  }

  function toDateStr(d: Date): string {
    return d.toISOString().split('T')[0];
  }

  // Normalize any date string to YYYY-MM-DD (handles ISO timestamps, date-only, etc.)
  function normalizeDateStr(val: any): string {
    if (!val) return '';
    if (typeof val === 'string') {
      // Already "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS..."
      return val.substring(0, 10);
    }
    // If it's a Date object somehow
    if (val instanceof Date && !isNaN(val.getTime())) {
      return val.toISOString().substring(0, 10);
    }
    return String(val).substring(0, 10);
  }

  const weekStart = toDateStr(getMondayOfWeek(todayDate)); // Monday of current week
  const weekEnd = toDateStr(getSundayOfWeek(todayDate));   // Sunday of current week

  // Month boundaries (Istanbul date based)
  const monthStart = `${todayDate.getUTCFullYear()}-${String(todayDate.getUTCMonth() + 1).padStart(2, '0')}-01`;
  const monthEndDate = new Date(Date.UTC(todayDate.getUTCFullYear(), todayDate.getUTCMonth() + 1, 0));
  const monthEnd = toDateStr(monthEndDate);

  // 30 days from today (for upcoming filter)
  const thirtyLaterDate = new Date(todayDate);
  thirtyLaterDate.setUTCDate(todayDate.getUTCDate() + 30);
  const thirtyLaterStr = toDateStr(thirtyLaterDate);

  // ─── Build Unified Records ──────────────────────────────

  const unifiedRecords = useMemo((): UnifiedRecord[] => {
    const records: UnifiedRecord[] = [];
    const linkedPromiseIds = new Set<string>();

    const promisesBySale: Record<string, any[]> = {};
    const promisesByNote: Record<string, any[]> = {};
    const activePromises = promises.filter(p =>
      p.status !== 'Ödeme Alındı' && p.status !== 'İptal' && p.status !== 'Sözünü Tutmadı'
    );
    activePromises.forEach(p => {
      if (p.sale_id) {
        if (!promisesBySale[p.sale_id]) promisesBySale[p.sale_id] = [];
        promisesBySale[p.sale_id].push(p);
        linkedPromiseIds.add(p.id);
      }
      if (p.promissory_note_id) {
        if (!promisesByNote[p.promissory_note_id]) promisesByNote[p.promissory_note_id] = [];
        promisesByNote[p.promissory_note_id].push(p);
        linkedPromiseIds.add(p.id);
      }
    });

    installments.forEach(inst => {
      if (inst.status === 'İptal') return;
      const cust = customerMap[inst.customer_id];
      const sale = saleMap[inst.sale_id];
      const salePromises = promisesBySale[inst.sale_id] || [];
      const bestPromise = salePromises.length > 0 ? salePromises[0] : null;
      const remaining = inst.remaining_amount !== undefined ? inst.remaining_amount : ((inst.amount || 0) - (inst.paid_amount || 0));

      records.push({
        id: inst.id,
        sourceType: 'installment',
        customerId: inst.customer_id || '',
        customerName: cust?.full_name || sale?.customer_name || '',
        customerPhone: cust?.phone || '',
        customerNo: cust?.customer_no || '',
        saleId: inst.sale_id || null,
        saleNo: sale?.sale_no || null,
        noteId: null,
        noteNo: null,
        dueDate: inst.due_date || '',
        totalAmount: inst.original_amount || inst.amount || 0,
        paidAmount: inst.paid_amount || 0,
        remainingAmount: remaining,
        status: inst.status || 'Bekliyor',
        installmentNo: inst.installment_no || inst.sequence_no,
        hasPromise: !!bestPromise,
        promiseId: bestPromise?.id || null,
        promiseAmount: bestPromise?.promised_amount || null,
        promiseDate: bestPromise?.promised_date || null,
        promiseStatus: bestPromise?.status || null,
        promiseAssignedUser: bestPromise?.assigned_user || null,
      });
    });

    notes.forEach(note => {
      if (note.status === 'İptal') return;
      const remaining = note.remaining_amount !== undefined ? note.remaining_amount : ((note.amount || 0) - (note.paid_amount || 0));
      const cust = customerMap[note.customer_id];
      const sale = saleMap[note.sale_id];
      const notePromises = promisesByNote[note.id] || [];
      const salePromises = note.sale_id ? (promisesBySale[note.sale_id] || []) : [];
      const allLinkedPromises = [...notePromises, ...salePromises];
      const bestPromise = allLinkedPromises.length > 0 ? allLinkedPromises[0] : null;
      if (bestPromise) linkedPromiseIds.add(bestPromise.id);

      records.push({
        id: note.id,
        sourceType: 'note',
        customerId: note.customer_id || '',
        customerName: cust?.full_name || note.customer_name || '',
        customerPhone: cust?.phone || note.customer_phone || '',
        customerNo: cust?.customer_no || '',
        saleId: note.sale_id || null,
        saleNo: sale?.sale_no || note.sale_no || null,
        noteId: note.id,
        noteNo: note.note_no || null,
        noteBookNo: note.note_book_no,
        notePageNo: note.note_page_no,
        dueDate: note.due_date || '',
        totalAmount: note.amount || 0,
        paidAmount: note.paid_amount || 0,
        remainingAmount: remaining,
        status: note.status || 'Bekliyor',
        hasPromise: !!bestPromise,
        promiseId: bestPromise?.id || null,
        promiseAmount: bestPromise?.promised_amount || null,
        promiseDate: bestPromise?.promised_date || null,
        promiseStatus: bestPromise?.status || null,
        promiseAssignedUser: bestPromise?.assigned_user || null,
      });
    });

    promises.forEach(p => {
      if (linkedPromiseIds.has(p.id)) return;
      if (p.status === 'Ödeme Alındı' || p.status === 'İptal') return;
      const cust = customerMap[p.customer_id];
      const sale = saleMap[p.sale_id];

      records.push({
        id: p.id,
        sourceType: 'promise',
        customerId: p.customer_id || '',
        customerName: cust?.full_name || '',
        customerPhone: cust?.phone || '',
        customerNo: cust?.customer_no || '',
        saleId: p.sale_id || null,
        saleNo: sale?.sale_no || null,
        noteId: p.promissory_note_id || null,
        noteNo: null,
        dueDate: p.promised_date || '',
        totalAmount: p.promised_amount || 0,
        paidAmount: p.paid_amount || 0,
        remainingAmount: (p.promised_amount || 0) - (p.paid_amount || 0),
        status: p.status || 'Bekliyor',
        hasPromise: true,
        promiseId: p.id,
        promiseAmount: p.promised_amount,
        promiseDate: p.promised_date,
        promiseStatus: p.status,
        promiseAssignedUser: p.assigned_user || null,
      });
    });

    records.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    return records;
  }, [installments, notes, promises, customerMap, saleMap]);

  const activeRecords = useMemo(() =>
    unifiedRecords.filter(r => r.remainingAmount > 0 && r.status !== 'Ödendi' && r.status !== 'Ödeme Alındı'),
    [unifiedRecords]
  );

  // ─── Tracking Tab - Summary Cards ───────────────────────

  const trackingSummary = useMemo(() => {
    const todayRecords = activeRecords.filter(r => r.dueDate === today);
    const weekRecords = activeRecords.filter(r => r.dueDate >= today && r.dueDate <= weekEnd);
    const monthRecords = activeRecords.filter(r => r.dueDate >= monthStart && r.dueDate <= monthEnd);
    const upcoming30 = activeRecords.filter(r => r.dueDate > today && r.dueDate <= thirtyLaterStr);
    const overdue = activeRecords.filter(r => r.dueDate < today && r.remainingAmount > 0);
    const allActivePromises = promises.filter(p =>
      p.status !== 'Ödeme Alındı' && p.status !== 'İptal' && p.status !== 'Sözünü Tutmadı'
    );
    const monthPayments = payments.filter((p: any) => {
      const d = normalizeDateStr(p.payment_date);
      return d >= monthStart && d <= monthEnd;
    });
    const monthCollected = monthPayments.reduce((s: number, p: any) => s + (p.amount || 0), 0);
    // MERKEZİ RPC'DEN GELEN TEK GERÇEK TOPLAM
    const totalPending = outstandingTotal;

    return {
      today: { count: todayRecords.length, amount: todayRecords.reduce((s, r) => s + r.remainingAmount, 0) },
      thisWeek: { count: weekRecords.length, amount: weekRecords.reduce((s, r) => s + r.remainingAmount, 0) },
      thisMonth: { count: monthRecords.length, amount: monthRecords.reduce((s, r) => s + r.remainingAmount, 0) },
      upcoming: { count: upcoming30.length, amount: upcoming30.reduce((s, r) => s + r.remainingAmount, 0) },
      overdue: { count: overdue.length, amount: overdue.reduce((s, r) => s + r.remainingAmount, 0) },
      promise: { count: allActivePromises.length, amount: allActivePromises.reduce((s: number, p: any) => s + ((p.promised_amount || 0) - (p.paid_amount || 0)), 0) },
      collected: { count: monthPayments.length, amount: monthCollected },
      pending: { count: activeRecords.length, amount: totalPending },
    };
  }, [activeRecords, promises, payments, today, weekEnd, monthStart, monthEnd, thirtyLaterStr, outstandingTotal]);

  // ─── Collected Tab - Summary Cards ──────────────────────

  const collectedSummary = useMemo(() => {
    const todayPayments = payments.filter((p: any) => normalizeDateStr(p.payment_date) === today);
    const weekPayments = payments.filter((p: any) => {
      const d = normalizeDateStr(p.payment_date);
      return d >= weekStart && d <= weekEnd;
    });
    const monthPayments = payments.filter((p: any) => {
      const d = normalizeDateStr(p.payment_date);
      return d >= monthStart && d <= monthEnd;
    });

    const totalCollected = payments.reduce((s: number, p: any) => s + (p.amount || 0), 0);

    return {
      today: { count: todayPayments.length, amount: todayPayments.reduce((s: number, p: any) => s + (p.amount || 0), 0) },
      thisWeek: { count: weekPayments.length, amount: weekPayments.reduce((s: number, p: any) => s + (p.amount || 0), 0) },
      thisMonth: { count: monthPayments.length, amount: monthPayments.reduce((s: number, p: any) => s + (p.amount || 0), 0) },
      total: { count: payments.length, amount: totalCollected },
    };
  }, [payments, today, weekStart, weekEnd, monthStart, monthEnd]);

  // ─── Collected Tab - Filtered Payments ──────────────────

  const filteredCollectedPayments = useMemo(() => {
    let result = [...payments];

    // Date filter
    switch (collectedFilter) {
      case 'today': result = result.filter((p: any) => normalizeDateStr(p.payment_date) === today); break;
      case 'thisWeek': result = result.filter((p: any) => {
        const d = normalizeDateStr(p.payment_date);
        return d >= weekStart && d <= weekEnd;
      }); break;
      case 'thisMonth': result = result.filter((p: any) => {
        const d = normalizeDateStr(p.payment_date);
        return d >= monthStart && d <= monthEnd;
      }); break;
      default: break;
    }

    // Search
    if (collectedSearch.trim()) {
      const q = toTurkishLower(collectedSearch.trim());
      result = result.filter((p: any) =>
        toTurkishLower(p.customer_name || '').includes(q) ||
        toTurkishLower(p.sale_no || '').includes(q) ||
        toTurkishLower(p.receipt_no || '').includes(q) ||
        toTurkishLower(p.received_by || '').includes(q) ||
        toTurkishLower(p.description || '').includes(q)
      );
    }

    // Sort by date descending (newest first)
    result.sort((a: any, b: any) => {
      const da = a.payment_date || '';
      const db = b.payment_date || '';
      return db.localeCompare(da);
    });

    return result;
  }, [payments, collectedFilter, collectedSearch, today, weekStart, weekEnd, monthStart, monthEnd]);

  const collectedTotalPages = Math.ceil(filteredCollectedPayments.length / collectedPageSize);
  const paginatedCollectedPayments = filteredCollectedPayments.slice(
    (collectedPage - 1) * collectedPageSize,
    collectedPage * collectedPageSize
  );

  useEffect(() => { setCollectedPage(1); }, [collectedFilter, collectedSearch]);

  // ─── Tracking Tab - Filtering & Search ──────────────────

  const filteredRecords = useMemo(() => {
    let result = [...activeRecords];

    switch (activeFilter) {
      case 'installment': result = result.filter(r => r.sourceType === 'installment'); break;
      case 'note': result = result.filter(r => r.sourceType === 'note'); break;
      case 'promise': result = result.filter(r => r.sourceType === 'promise'); break;
      case 'today': result = result.filter(r => r.dueDate === today); break;
      case 'thisWeek': result = result.filter(r => r.dueDate >= today && r.dueDate <= weekEnd); break;
      case 'thisMonth': result = result.filter(r => r.dueDate >= monthStart && r.dueDate <= monthEnd); break;
      case 'upcoming': result = result.filter(r => r.dueDate > today && r.dueDate <= thirtyLaterStr); break;
      case 'overdue': result = result.filter(r => r.dueDate < today && r.remainingAmount > 0); break;
      case 'paid': result = []; break;
      default: break;
    }

    if (search.trim()) {
      const q = toTurkishLower(search.trim());
      result = result.filter(r =>
        toTurkishLower(r.customerName).includes(q) ||
        r.customerPhone.includes(q) ||
        toTurkishLower(r.customerNo).includes(q) ||
        toTurkishLower(r.saleNo || '').includes(q) ||
        toTurkishLower(r.noteNo || '').includes(q)
      );
    }

    return result;
  }, [unifiedRecords, activeRecords, activeFilter, search, today, weekEnd, monthStart, monthEnd, thirtyLaterStr]);

  const totalPages = Math.ceil(filteredRecords.length / pageSize);
  const paginatedRecords = filteredRecords.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => { setCurrentPage(1); }, [activeFilter, search]);

  // ─── Handlers ───────────────────────────────────────────

  const handleCardClick = (key: FilterKey) => {
    if (key === 'paid') {
      // "Ödenen" kartı → direkt "Alınan Ödemeler" sekmesine geç
      setActiveTab('collected');
      return;
    }
    setActiveFilter(prev => prev === key ? 'all' : key);
  };

  // "Ödenen" filtresi seçilince otomatik olarak "Alınan Ödemeler" sekmesine geç
  useEffect(() => {
    if (activeFilter === 'paid') {
      setActiveTab('collected');
      setActiveFilter('all');
    }
  }, [activeFilter]);

  const handleTakePayment = (record: UnifiedRecord) => {
    setPaymentTarget({
      customerId: record.customerId,
      saleId: record.saleId || undefined,
      amount: record.hasPromise ? (record.promiseAmount || undefined) : (record.remainingAmount || undefined),
    });
    setShowPaymentModal(true);
  };

  const handlePaymentCreated = async () => {
    setShowPaymentModal(false);
    setPaymentTarget(null);
    await loadData();
    showToast('Ödeme başarıyla kaydedildi. Taksit, satış, senet ve ödeme sözleri güncellendi.', 'success');
  };

  // ─── Filter Tabs Config ─────────────────────────────────

  const filters: { key: FilterKey; label: string; icon: string }[] = [
    { key: 'all', label: 'Tümü', icon: 'ri-list-check-2' },
    { key: 'installment', label: 'Taksitler', icon: 'ri-calendar-check-line' },
    { key: 'note', label: 'Senetler', icon: 'ri-file-list-3-line' },
    { key: 'promise', label: 'Ödeme Sözleri', icon: 'ri-hand-heart-line' },
    { key: 'today', label: 'Bugün', icon: 'ri-calendar-line' },
    { key: 'thisWeek', label: 'Bu Hafta', icon: 'ri-calendar-2-line' },
    { key: 'thisMonth', label: 'Bu Ay', icon: 'ri-calendar-todo-line' },
    { key: 'overdue', label: 'Geciken', icon: 'ri-alert-line' },
    { key: 'paid', label: 'Ödenen', icon: 'ri-check-double-line' },
  ];

  // ─── Collected Filters ──────────────────────────────────

  const collectedFilters: { key: CollectedFilter; label: string; icon: string }[] = [
    { key: 'all', label: 'Tümü', icon: 'ri-list-check-2' },
    { key: 'today', label: 'Bugün', icon: 'ri-calendar-line' },
    { key: 'thisWeek', label: 'Bu Hafta', icon: 'ri-calendar-2-line' },
    { key: 'thisMonth', label: 'Bu Ay', icon: 'ri-calendar-todo-line' },
  ];

  // ─── Loading ────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div><h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Tahsilat Merkezi</h2><p className="text-sm text-foreground-400 mt-0.5">Yükleniyor...</p></div>
        </div>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
          <p className="text-sm text-foreground-400">Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div><h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Tahsilat Merkezi</h2></div>
        </div>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl text-red-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-foreground-700">Yükleme başarısız</h3>
          <p className="text-sm text-foreground-400">{error}</p>
          <button className="btn-primary mt-2 cursor-pointer" onClick={loadData}>Tekrar Dene</button>
        </div>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Tahsilat Merkezi</h2>
          <p className="text-sm text-foreground-400 mt-0.5">
            {activeTab === 'tracking'
              ? `${filteredRecords.length} kayıt · Toplam Bekleyen: ${formatTRY(trackingSummary.pending.amount)}`
              : `${filteredCollectedPayments.length} tahsilat · Toplam: ${formatTRY(collectedSummary.total.amount)}`
            }
          </p>
        </div>
        <button
          onClick={() => navigate('/payments/new')}
          className="btn-primary flex items-center gap-2 self-start cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i><span>Yeni Ödeme</span>
        </button>
      </div>

      {/* Tab Switcher */}
      <div className="bg-background-100 rounded-full p-1 inline-flex">
        <button
          onClick={() => setActiveTab('tracking')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'tracking'
              ? 'bg-background-50 text-foreground-900 shadow-sm'
              : 'text-foreground-400 hover:text-foreground-600'
          }`}
        >
          <i className="ri-timer-line"></i>Tahsilat Takibi
        </button>
        <button
          onClick={() => setActiveTab('collected')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
            activeTab === 'collected'
              ? 'bg-background-50 text-foreground-900 shadow-sm'
              : 'text-foreground-400 hover:text-foreground-600'
          }`}
        >
          <i className="ri-check-double-line"></i>Alınan Ödemeler
        </button>
      </div>

      {/* ─── TRACKING TAB ─────────────────────────────────── */}
      {activeTab === 'tracking' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard
              title="Bugün Ödenecekler"
              count={trackingSummary.today.count}
              amount={trackingSummary.today.amount}
              icon="ri-calendar-check-line"
              colorClass="bg-amber-400"
              active={activeFilter === 'today'}
              onClick={() => handleCardClick('today')}
            />
            <SummaryCard
              title="Bu Hafta Ödenecekler"
              count={trackingSummary.thisWeek.count}
              amount={trackingSummary.thisWeek.amount}
              icon="ri-calendar-line"
              colorClass="bg-primary-400"
              active={activeFilter === 'thisWeek'}
              onClick={() => handleCardClick('thisWeek')}
            />
            <SummaryCard
              title="Bu Ay Ödenecekler"
              count={trackingSummary.thisMonth.count}
              amount={trackingSummary.thisMonth.amount}
              icon="ri-calendar-2-line"
              colorClass="bg-accent-400"
              active={activeFilter === 'thisMonth'}
              onClick={() => handleCardClick('thisMonth')}
            />
            <SummaryCard
              title="Yaklaşan Ödemeler"
              count={trackingSummary.upcoming.count}
              amount={trackingSummary.upcoming.amount}
              icon="ri-time-line"
              colorClass="bg-secondary-400"
              active={activeFilter === 'upcoming'}
              onClick={() => handleCardClick('upcoming')}
            />
            <SummaryCard
              title="Geciken Ödemeler"
              count={trackingSummary.overdue.count}
              amount={trackingSummary.overdue.amount}
              icon="ri-alert-line"
              colorClass="bg-red-400"
              active={activeFilter === 'overdue'}
              onClick={() => handleCardClick('overdue')}
            />
            <SummaryCard
              title="Ödeme Sözü Verenler"
              count={trackingSummary.promise.count}
              amount={trackingSummary.promise.amount}
              icon="ri-hand-heart-line"
              colorClass="bg-pink-400"
              active={activeFilter === 'promise'}
              onClick={() => handleCardClick('promise')}
            />
            <SummaryCard
              title="Bu Ay Tahsil Edilen"
              count={trackingSummary.collected.count}
              amount={trackingSummary.collected.amount}
              icon="ri-bank-card-line"
              colorClass="bg-emerald-400"
              active={activeFilter === 'paid'}
              onClick={() => handleCardClick('paid')}
            />
            <SummaryCard
              title="Toplam Bekleyen Tahsilat"
              count={trackingSummary.pending.count}
              amount={trackingSummary.pending.amount}
              icon="ri-exchange-funds-line"
              colorClass="bg-orange-400"
              active={activeFilter === 'all'}
              onClick={() => setActiveFilter('all')}
            />
          </div>

          {/* Filters */}
          <div className="bg-background-100 rounded-xl p-1 overflow-x-auto scrollbar-none">
            <div className="flex items-center gap-1 min-w-max">
              {filters.map(f => (
                <button
                  key={f.key}
                  onClick={() => setActiveFilter(activeFilter === f.key ? 'all' : f.key)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeFilter === f.key
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  <i className={`${f.icon} ${activeFilter === f.key ? 'text-primary-500' : ''}`}></i>
                  <span>{f.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Müşteri adı, telefon, müşteri no, satış no, senet no ile ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
            />
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
          </div>

          {/* Records List */}
          <div className="space-y-2">
            {paginatedRecords.length === 0 ? (
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-10 flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm text-foreground-400">
                  {activeFilter === 'paid' ? 'Ödenmiş kayıt bulunamadı' : 'Bu kategoride bekleyen tahsilat kaydı yok'}
                </p>
              </div>
            ) : (
              paginatedRecords.map(record => (
                <div
                  key={`${record.sourceType}-${record.id}`}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-4 hover:border-background-300 transition-all"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <button
                          onClick={() => navigate(`/customers/${record.customerId}`)}
                          className="text-sm font-semibold text-foreground-900 hover:text-primary-500 cursor-pointer truncate max-w-[200px]"
                        >
                          {record.customerName || 'Bilinmeyen Müşteri'}
                        </button>
                        <SourceBadge type={record.sourceType} />
                        {record.status !== 'Ödendi' && record.status !== 'Ödeme Alındı' && (
                          <StatusBadge status={record.status} />
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-foreground-400 flex-wrap">
                        {record.customerNo && <span>No: {record.customerNo}</span>}
                        {record.customerPhone && (
                          <>
                            <span>{formatPhone(record.customerPhone)}</span>
                            <a
                              href={getTelUrl(record.customerPhone)}
                              className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center cursor-pointer hover:bg-blue-200 transition-colors"
                              title="Ara"
                            >
                              <i className="ri-phone-line text-xs text-blue-600"></i>
                            </a>
                            <a
                              href={getWhatsAppUrl(record.customerPhone)}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center cursor-pointer hover:bg-emerald-200 transition-colors"
                              title="WhatsApp"
                            >
                              <i className="ri-whatsapp-line text-xs text-emerald-600"></i>
                            </a>
                          </>
                        )}
                        {record.saleNo && (
                          <button onClick={() => navigate(`/sales/${record.saleId}`)} className="text-primary-500 hover:text-primary-600 cursor-pointer font-medium">
                            {record.saleNo}
                          </button>
                        )}
                        {record.noteNo && (
                          <span className="text-accent-600 font-medium">{record.noteNo}</span>
                        )}
                        {record.noteBookNo && record.notePageNo && (
                          <span className="text-foreground-300">Ktp: {record.noteBookNo} / S: {record.notePageNo}</span>
                        )}
                      </div>

                      {record.hasPromise && record.sourceType !== 'promise' && (
                        <div className="mt-2 bg-pink-50/50 border border-pink-100 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 flex-wrap text-xs">
                            <span className="text-pink-600 font-medium flex items-center gap-1">
                              <i className="ri-hand-heart-line"></i>Ödeme Sözü:
                            </span>
                            <span className="text-foreground-700">{record.promiseDate}</span>
                            <span className="text-foreground-700 font-medium">{formatTRY(record.promiseAmount || 0)}</span>
                            {record.promiseStatus && <StatusBadge status={record.promiseStatus} />}
                            {record.promiseAssignedUser && (
                              <span className="text-foreground-400">· {record.promiseAssignedUser}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-4 lg:gap-6 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-xs text-foreground-400">Vade / Söz Tarihi</p>
                        <p className="text-sm font-medium text-foreground-700">{record.dueDate ? new Date(record.dueDate).toLocaleDateString('tr-TR') : '—'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-foreground-400">Tutar</p>
                        <p className="text-sm font-semibold text-foreground-800">{formatTRY(record.totalAmount)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-foreground-400">Ödenen</p>
                        <p className="text-sm font-semibold text-emerald-600">{record.paidAmount > 0 ? formatTRY(record.paidAmount) : '—'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-foreground-400">Kalan</p>
                        <p className={`text-sm font-bold ${record.remainingAmount > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                          {formatTRY(record.remainingAmount)}
                        </p>
                      </div>

                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {record.remainingAmount > 0 && (
                          <button
                            onClick={() => handleTakePayment(record)}
                            className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center cursor-pointer hover:bg-emerald-100 transition-colors"
                            title="Ödeme Al"
                          >
                            <i className="ri-bank-card-line text-sm text-emerald-500"></i>
                          </button>
                        )}
                        {(record.sourceType === 'installment' && record.saleId) && (
                          <button
                            onClick={() => navigate(`/sales/${record.saleId}`)}
                            className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-50 transition-colors"
                            title="Satışa Git"
                          >
                            <i className="ri-shopping-cart-line text-sm text-foreground-500"></i>
                          </button>
                        )}
                        {(record.sourceType === 'note') && (
                          <button
                            onClick={() => navigate('/promissory-notes')}
                            className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-accent-50 transition-colors"
                            title="Senetlere Git"
                          >
                            <i className="ri-file-list-3-line text-sm text-foreground-500"></i>
                          </button>
                        )}
                        {(record.sourceType === 'promise') && (
                          <button
                            onClick={() => navigate('/payment-promises')}
                            className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-pink-50 transition-colors"
                            title="Söz Detayı"
                          >
                            <i className="ri-hand-heart-line text-sm text-foreground-500"></i>
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/customers/${record.customerId}`)}
                          className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-foreground-100 transition-colors"
                          title="Müşteri Detayı"
                        >
                          <i className="ri-user-line text-sm text-foreground-500"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-foreground-400">Sayfa {currentPage} / {totalPages}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-30"
                >
                  <i className="ri-arrow-left-s-line text-sm"></i>
                </button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium cursor-pointer ${
                        pageNum === currentPage ? 'bg-primary-500 text-background-50' : 'hover:bg-background-100 text-foreground-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-30"
                >
                  <i className="ri-arrow-right-s-line text-sm"></i>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ─── COLLECTED TAB ────────────────────────────────── */}
      {activeTab === 'collected' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard
              title="Bugün Alınan"
              count={collectedSummary.today.count}
              amount={collectedSummary.today.amount}
              icon="ri-calendar-check-line"
              colorClass="bg-emerald-400"
              active={collectedFilter === 'today'}
              onClick={() => setCollectedFilter(collectedFilter === 'today' ? 'all' : 'today')}
            />
            <SummaryCard
              title="Bu Hafta Alınan"
              count={collectedSummary.thisWeek.count}
              amount={collectedSummary.thisWeek.amount}
              icon="ri-calendar-2-line"
              colorClass="bg-primary-400"
              active={collectedFilter === 'thisWeek'}
              onClick={() => setCollectedFilter(collectedFilter === 'thisWeek' ? 'all' : 'thisWeek')}
            />
            <SummaryCard
              title="Bu Ay Alınan"
              count={collectedSummary.thisMonth.count}
              amount={collectedSummary.thisMonth.amount}
              icon="ri-calendar-todo-line"
              colorClass="bg-accent-400"
              active={collectedFilter === 'thisMonth'}
              onClick={() => setCollectedFilter(collectedFilter === 'thisMonth' ? 'all' : 'thisMonth')}
            />
            <SummaryCard
              title="Toplam Tahsilat"
              count={collectedSummary.total.count}
              amount={collectedSummary.total.amount}
              icon="ri-exchange-funds-line"
              colorClass="bg-emerald-500"
              active={collectedFilter === 'all'}
              onClick={() => setCollectedFilter('all')}
            />
          </div>

          {/* Filters */}
          <div className="bg-background-100 rounded-xl p-1 overflow-x-auto scrollbar-none">
            <div className="flex items-center gap-1 min-w-max">
              {collectedFilters.map(f => (
                <button
                  key={f.key}
                  onClick={() => setCollectedFilter(collectedFilter === f.key ? 'all' : f.key)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    collectedFilter === f.key
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  <i className={`${f.icon} ${collectedFilter === f.key ? 'text-primary-500' : ''}`}></i>
                  <span>{f.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Müşteri adı, satış no, makbuz no, tahsil eden ile ara..."
              value={collectedSearch}
              onChange={e => setCollectedSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
            />
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
          </div>

          {/* Collected Payments List */}
          <div className="space-y-2">
            {paginatedCollectedPayments.length === 0 ? (
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-10 flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm text-foreground-400">Bu dönemde tahsilat kaydı bulunamadı</p>
              </div>
            ) : (
              paginatedCollectedPayments.map((p: any) => {
                const cust = customerMap[p.customer_id];
                const sale = saleMap[p.sale_id];
                const note = noteMap[p.promissory_note_id];
                return (
                  <div
                    key={p.id}
                    className="bg-background-50 rounded-xl border border-background-200/70 p-4 hover:border-background-300 transition-all"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                      {/* Left: Customer & Reference info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <button
                            onClick={() => navigate(`/customers/${p.customer_id}`)}
                            className="text-sm font-semibold text-foreground-900 hover:text-primary-500 cursor-pointer truncate max-w-[200px]"
                          >
                            {p.customer_name || cust?.full_name || 'Bilinmeyen Müşteri'}
                          </button>
                          <PaymentMethodBadge method={p.payment_method || 'Nakit'} />
                          <SourceTypeBadge type={p.source_type} />
                          {p.is_legacy_payment && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 font-medium whitespace-nowrap">
                              Devir
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-foreground-400 flex-wrap">
                          {cust?.customer_no && <span>No: {cust.customer_no}</span>}
                          {cust?.phone && (
                            <>
                              <span>{formatPhone(cust.phone)}</span>
                              <a
                                href={getTelUrl(cust.phone)}
                                className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center cursor-pointer hover:bg-blue-200 transition-colors"
                                title="Ara"
                              >
                                <i className="ri-phone-line text-xs text-blue-600"></i>
                              </a>
                              <a
                                href={getWhatsAppUrl(cust.phone)}
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center cursor-pointer hover:bg-emerald-200 transition-colors"
                                title="WhatsApp"
                              >
                                <i className="ri-whatsapp-line text-xs text-emerald-600"></i>
                              </a>
                            </>
                          )}
                          {p.sale_no && (
                            <button onClick={() => navigate(`/sales/${p.sale_id}`)} className="text-primary-500 hover:text-primary-600 cursor-pointer font-medium">
                              {p.sale_no}
                            </button>
                          )}
                          {note?.note_no && (
                            <span className="text-accent-600 font-medium">{note.note_no}</span>
                          )}
                          {p.receipt_no && (
                            <span className="bg-foreground-100 px-2 py-0.5 rounded text-foreground-500 font-mono text-[11px]">#{p.receipt_no}</span>
                          )}
                        </div>
                        {p.description && (
                          <p className="mt-1.5 text-xs text-foreground-400 line-clamp-1">{p.description}</p>
                        )}
                      </div>

                      {/* Right: Amounts & Details */}
                      <div className="flex items-center gap-4 lg:gap-6 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-xs text-foreground-400">Tarih</p>
                          <p className="text-sm font-medium text-foreground-700">
                            {p.payment_date ? new Date(p.payment_date).toLocaleDateString('tr-TR') : '—'}
                          </p>
                        </div>
                        <div className="text-right min-w-[100px]">
                          <p className="text-xs text-foreground-400">Tutar</p>
                          <p className="text-sm font-bold text-emerald-600">{formatTRY(p.amount || 0)}</p>
                        </div>
                        {p.received_by && (
                          <div className="text-right hidden sm:block">
                            <p className="text-xs text-foreground-400">Tahsil Eden</p>
                            <p className="text-sm font-medium text-foreground-600">{p.received_by}</p>
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {p.sale_id && (
                            <button
                              onClick={() => navigate(`/sales/${p.sale_id}`)}
                              className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-50 transition-colors"
                              title="Satışa Git"
                            >
                              <i className="ri-shopping-cart-line text-sm text-foreground-500"></i>
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/customers/${p.customer_id}`)}
                            className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-foreground-100 transition-colors"
                            title="Müşteri Detayı"
                          >
                            <i className="ri-user-line text-sm text-foreground-500"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {collectedTotalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-foreground-400">Sayfa {collectedPage} / {collectedTotalPages}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCollectedPage(p => Math.max(1, p - 1))}
                  disabled={collectedPage === 1}
                  className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-30"
                >
                  <i className="ri-arrow-left-s-line text-sm"></i>
                </button>
                {Array.from({ length: Math.min(collectedTotalPages, 7) }, (_, i) => {
                  let pageNum: number;
                  if (collectedTotalPages <= 7) {
                    pageNum = i + 1;
                  } else if (collectedPage <= 4) {
                    pageNum = i + 1;
                  } else if (collectedPage >= collectedTotalPages - 3) {
                    pageNum = collectedTotalPages - 6 + i;
                  } else {
                    pageNum = collectedPage - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCollectedPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium cursor-pointer ${
                        pageNum === collectedPage ? 'bg-primary-500 text-background-50' : 'hover:bg-background-100 text-foreground-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCollectedPage(p => Math.min(collectedTotalPages, p + 1))}
                  disabled={collectedPage === collectedTotalPages}
                  className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-30"
                >
                  <i className="ri-arrow-right-s-line text-sm"></i>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Payment Modal */}
      {paymentTarget && (
        <PaymentFormModal
          open={showPaymentModal}
          onClose={() => { setShowPaymentModal(false); setPaymentTarget(null); }}
          mode="customer"
          customerId={paymentTarget.customerId}
          saleId={paymentTarget.saleId}
          initialAmount={paymentTarget.amount}
          onPaymentCreated={handlePaymentCreated}
        />
      )}
    </div>
  );
}